// src/tabs/logements/FormulairesTab.jsx — Phase 2.E.5 (ADR-038, ADR-065, ADR-071, ADR-072, ADR-074)
// 1 formulaire unifié éditable par logement (fusion welcome+checkin).
// Champs configurables (label, type, required, reference) + drag-reorder natif HTML5.
// Photo de couverture optionnelle (base64, 1MB).

import { useState, useEffect, useCallback, useRef } from "react"
import { Field, FieldFull, Inp, Select, Textarea, SectionCard } from "./atoms"
import { DS } from "../../lib/ui"
import { createFormToken, listFormTokens, publicFormUrl, deleteFormToken } from "../../lib/forms-api"
// CHANTIER LIEN COURT — les liens COPIÉS (envoyés à la main au voyageur) passent
// par le lien court `/l/<code>`, avec repli automatique sur le lien long en cas
// d'échec d'émission. `publicFormUrl` reste la source du lien CLIQUABLE « Voir ».
// ⚠️ `copyGuestLinkNow` écrit le presse-papier SYNCHRONEMENT (fenêtre
// d'activation utilisateur) : ne JAMAIS insérer d'`await` avant son appel.
import { copyGuestLinkNow } from "../../lib/guest-links-api"
import { migrateLegacyToUnified, getIdDocPolicy, getBillingAddressPolicy } from "../../lib/forms-migration"
import { resolveCoverPhoto, uploadCoverPhoto, clearCoverPhotoPatch } from "../../lib/form-cover-photo"
import { staticFieldLabel, staticContentTr, normalizeFormLocale } from "../../lib/form-i18n"
// LOT E V4 — l'aperçu voyageur rend le VRAI composant du formulaire public
// (GuestFormView), en mode preview inerte (aucune soumission, aucun token).
import { substituteVars } from "../../components/GuestFormView"
// REFONTE V2 (plan FORMULAIRE_REFONTE_PLAN_V2, pièce 4) — l'aperçu admin
// bascule sur le composant V2 dès le merge : aperçu INERTE, indépendant du
// drapeau voyageur form_v2 (l'admin valide ce que la V2 rendra).
import GuestFormViewV2 from "../../components/GuestFormViewV2"
import {
  Settings, IdCard, Link2, ClipboardList, GripVertical, Trash2, Plus,
  ImagePlus, Pencil, Eye, Copy, Link as LinkIcon, Info, Lightbulb, CornerDownLeft,
  FileText, ExternalLink, CheckCircle2,
} from "lucide-react"

// ── Rhabillage DA (DESIGN.md §8 ter « éditeur + aperçu ») — habillage SEUL.
// Pastille de teinte-catégorie portant l'icône lucide (repère de navigation
// FIXE, cf. §2). « réglages/configuration » = ardoise ; le reste suit le set
// ciel/indigo/corail doux. L'icône (forme) reste le repère premier.
function catPill(Ico, bg, ico) {
  return (
    <span style={{
      display: "inline-flex", width: 26, height: 26, borderRadius: 7,
      background: bg, color: ico, alignItems: "center", justifyContent: "center",
      verticalAlign: "middle", marginRight: 2,
    }}>
      <Ico size={15} strokeWidth={1.8} />
    </span>
  )
}

// ── Reskin LOT A — cartes repliables (cohérent Instructions / Info FBZ) ───────
// Pur layout : SectionCard repliable d'atoms.jsx + état session global, défaut
// TOUT FERMÉ. Aucun changement fonctionnel (tokens / policy / champs / upload
// inchangés). Clé de session dédiée (≠ Instructions / Info FBZ).
const FORM_CARDS_KEY = "fbz-formulaires-cards-open"
const FORM_CARD_IDS = ["config", "piece", "liens", "champs"]
function readFormCards() {
  const allClosed = () => Object.fromEntries(FORM_CARD_IDS.map((id) => [id, false]))
  try {
    const raw = sessionStorage.getItem(FORM_CARDS_KEY)
    if (!raw) return allClosed()
    const parsed = JSON.parse(raw)
    const base = allClosed()
    for (const id of FORM_CARD_IDS) {
      if (typeof parsed?.[id] === "boolean") base[id] = parsed[id]
    }
    return base
  } catch (_) {
    return allClosed()
  }
}

// Teintes-catégorie (DA §2/§8 ter, maquette #2) : pastille d'icône lucide + bande
// de titre pastel ; fond de carte blanc franc (la couleur ne barbouille pas la
// carte). Réglages = ardoise · Pièce d'identité = indigo · Liens publics = corail
// doux · Champs = ciel. L'icône (forme) reste le repère premier.
const FORM_CARD_META = {
  config: { icon: catPill(Settings, DS.catArdoiseBg, DS.catArdoiseIco),    title: "Configuration générale", accent: DS.catArdoiseIco, titleColor: DS.marineDark, headerBg: DS.catArdoiseBg, bg: DS.card },
  piece:  { icon: catPill(IdCard, DS.catIndigoBg, DS.catIndigoIco),        title: "Pièce d'identité",        accent: DS.catIndigoIco,  titleColor: DS.marineDark, headerBg: DS.catIndigoBg,  bg: DS.card },
  liens:  { icon: catPill(Link2, DS.catCorailBg, DS.catCorailIco),         title: "Liens publics",           accent: DS.catCorailIco,  titleColor: DS.marineDark, headerBg: DS.catCorailBg,  bg: DS.card },
  champs: { icon: catPill(ClipboardList, DS.catCielBg, DS.catCielIco),     title: "Champs du formulaire",    accent: DS.catCielIco,    titleColor: DS.marineDark, headerBg: DS.catCielBg,    bg: DS.card },
}
const FORM_CARD_SUBTITLES = {
  config: "Sert à : titre + introduction affichés en haut du formulaire voyageur · photo de couverture (hero) · URL de redirection après envoi.",
  piece:  "Sert à : afficher ou non le module pièce d'identité (type + photos) dans le formulaire selon le canal du voyageur (Direct / Booking / Airbnb).",
  liens:  "Sert à : générer et révoquer les liens /bienvenue à envoyer au voyageur (un lien = un accès au formulaire, sans compte).",
  champs: "Sert à : les champs réellement affichés au voyageur · l'ordre = ordre d'affichage. Glisser-déposer pour réorganiser.",
}

// Chip d'état affiché dans l'en-tête d'accordéon (maquette #2). Pur habillage :
// dérivé de props déjà disponibles (config / id_doc_policy), aucun appel data.
function StatusChip({ tone = "neutral", children }) {
  const TONES = {
    success: { bg: DS.successBg, fg: DS.successText },
    neutral: { bg: DS.toolBg,    fg: DS.textSecondary },
    ciel:    { bg: DS.cielSoft,  fg: DS.marine },
    corail:  { bg: DS.corailSoft, fg: DS.corailText },
  }
  const c = TONES[tone] || TONES.neutral
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: c.bg, color: c.fg, fontSize: 10, fontWeight: 700,
      padding: "2px 8px", borderRadius: 999, fontFamily: DS.fontBody,
      letterSpacing: 0.2, whiteSpace: "nowrap", flex: "0 0 auto",
    }}>
      {children}
    </span>
  )
}

// Sous-titre d'accordéon = chip d'état + phrase d'aide (la phrase reste l'état
// réel, cf. règle « textes d'aide = état actuel »).
function subWithChip(chip, text) {
  return (
    <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
      {chip}
      <span>{text}</span>
    </span>
  )
}

// Regroupement VISUEL des champs (option a — sans toucher le modèle plat
// unified.fields[]). Miroir de PublicFormPage : « Documents légaux » regroupe le
// module pièce d'identité ; tout le reste (dont billing_address) = « Coordonnées ».
const DOC_FIELD_REFS = new Set(["id_type", "passport_photo", "id_recto", "id_verso"])

const FIELD_TYPES = [
  { value: 'input',    label: 'Texte (input)' },
  { value: 'textarea', label: 'Texte long (textarea)' },
  { value: 'dropdown', label: 'Liste déroulante' },
  { value: 'radio',    label: 'Boutons radio' },
  { value: 'checkbox', label: 'Cases à cocher' },
  { value: 'date',     label: 'Date' },
  { value: 'time',     label: 'Heure' },
  { value: 'file',     label: 'Fichier' },
]
const INPUT_TYPES = [
  { value: 'text',     label: 'text' },
  { value: 'email',    label: 'email' },
  { value: 'tel',      label: 'tel' },
  { value: 'number',   label: 'number' },
  { value: 'password', label: 'password' },
]

// Helpers de migration extraits dans src/lib/forms-migration.js (ADR-074)
// pour partage avec PublicFormPage (voyageur). Idempotents, pas de save auto.

function newId(prefix) {
  return prefix + Date.now().toString(36).slice(-5) + Math.random().toString(36).slice(2, 5)
}

function reorder(items, fromIdx, toIdx) {
  const next = [...items]
  const [moved] = next.splice(fromIdx, 1)
  next.splice(toIdx, 0, moved)
  return next.map((it, i) => ({ ...it, order: i }))
}


export default function FormulairesTab({ log, onUpdate }) {
  const forms = log.forms || {}
  const unified = migrateLegacyToUnified(forms)
  const hasLegacyData = !forms.unified && !!(forms.welcome || forms.checkin)
  const hasLegacySections = !!forms.unified?.sections && !forms.unified?.fields

  function saveUnified(next) {
    onUpdate('forms', { ...forms, unified: next })
  }

  // ADR-189 amendement — policy pièce d'identité PAR CANAL (direct/booking/airbnb).
  // Persistée dans forms.id_doc_policy via le mécanisme d'enregistrement existant.
  const idDocPolicy = getIdDocPolicy(forms)
  function setIdDocChannel(channel, enabled) {
    onUpdate('forms', { ...forms, id_doc_policy: { ...idDocPolicy, [channel]: enabled } })
  }

  function updateGlobal(field, value) {
    saveUnified({ ...unified, [field]: value })
  }

  function updateField(fieldIdx, patch) {
    saveUnified({
      ...unified,
      fields: unified.fields.map((f, j) => j === fieldIdx ? { ...f, ...patch } : f),
    })
  }

  function addField() {
    const blank = { id: newId('fld_'), label: 'Nouveau champ', type: 'input', input_type: 'text', required: false, reference: '' }
    saveUnified({ ...unified, fields: [...unified.fields, blank] })
  }

  function removeField(fieldIdx) {
    if (!window.confirm('Supprimer ce champ ?')) return
    saveUnified({ ...unified, fields: unified.fields.filter((_, j) => j !== fieldIdx) })
  }

  function dropAt(toIdx, fromIdx) {
    saveUnified({ ...unified, fields: reorder(unified.fields, fromIdx, toIdx) })
  }

  // Cartes repliables — état persistant SESSION global, défaut TOUT FERMÉ
  // (cohérent Instructions / Info FBZ). Toggle : undefined/true → false, false → true.
  const [openCards, setOpenCards] = useState(readFormCards)
  useEffect(() => {
    try {
      sessionStorage.setItem(FORM_CARDS_KEY, JSON.stringify(openCards))
    } catch (_) {}
  }, [openCards])
  const toggleCard = useCallback((cardId) => {
    setOpenCards((prev) => ({ ...prev, [cardId]: prev[cardId] === false }))
  }, [])

  const fieldCount = unified.fields?.length || 0

  // ── Chips d'état (dérivés, 0 data) + CTA « Ouvrir le formulaire » ───────────
  const enabledChannels = ID_DOC_CHANNELS.filter((ch) => idDocPolicy[ch.key]).length
  const configReady = !!((unified.title || "").trim() && (unified.description || "").trim())
  // Le CTA d'en-tête déplie la carte « Liens publics » (mécanisme réel pour
  // générer/copier le lien partageable à envoyer au voyageur) et y défile.
  const liensRef = useRef(null)
  const openLinks = useCallback(() => {
    setOpenCards((prev) => ({ ...prev, liens: true }))
    requestAnimationFrame(() => {
      try {
        liensRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      } catch (_) {
        // scrollIntoView indisponible (jsdom / navigateurs anciens) — non bloquant.
      }
    })
  }, [])

  return (
    <div>
      {hasLegacyData && (
        <div style={{display:"flex",gap:9,alignItems:"flex-start",padding:"9px 12px",background:DS.warningBg,border:`0.5px solid #f1d9ac`,borderLeft:`3px solid ${DS.warning}`,borderRadius:8,fontSize:11.5,color:DS.warningText,lineHeight:1.5,marginBottom:14}}>
          <Lightbulb size={15} strokeWidth={1.8} style={{flex:"0 0 auto",marginTop:1}} />
          <span><strong>Migration formulaires</strong> — les anciens formulaires Bienvenue + Check-in sont fusionnés ci-dessous. La fusion sera persistée au premier enregistrement. Les liens publics anciens (welcome/checkin) restent valides en parallèle (rétrocompat ADR-065).</span>
        </div>
      )}
      {hasLegacySections && (
        <div style={{display:"flex",gap:9,alignItems:"flex-start",padding:"9px 12px",background:DS.warningBg,border:`0.5px solid #f1d9ac`,borderLeft:`3px solid ${DS.warning}`,borderRadius:8,fontSize:11.5,color:DS.warningText,lineHeight:1.5,marginBottom:14}}>
          <Lightbulb size={15} strokeWidth={1.8} style={{flex:"0 0 auto",marginTop:1}} />
          <span><strong>Format simplifié (ADR-071)</strong> — les 2 sections "Avant arrivée" + "À l'arrivée" sont fusionnées en 1 bloc unique. Au premier enregistrement, le format sera persisté.</span>
        </div>
      )}

      {/* ─── En-tête : statut du formulaire + CTA « Ouvrir le formulaire » ─── */}
      <FormHeaderBand active={fieldCount > 0} onOpen={openLinks} />

      {/* ─── Maquette #2 : éditeur (gauche) | aperçu voyageur (droite) ───
          Layout CSS-first (flex + wrap) : sous ~800px le panneau d'aperçu passe
          sous l'éditeur. Pas de minWidth:0 sur les blocs multi-lignes. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        {/* ── Colonne éditeur ── */}
        <div style={{ flex: "1 1 440px" }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: DS.textMuted, fontFamily: DS.fontBody, margin: "2px 2px 10px" }}>
            Configuration
          </div>

          {/* ═════ Configuration générale (ardoise) ═════ */}
          <SectionCard
            {...FORM_CARD_META.config}
            subtitle={subWithChip(
              <StatusChip tone={configReady ? "success" : "neutral"}>{configReady ? "Complète" : "À compléter"}</StatusChip>,
              FORM_CARD_SUBTITLES.config,
            )}
            collapsible open={openCards.config} onToggle={() => toggleCard("config")}
          >
          <FieldFull label="Titre du formulaire">
            <Inp value={unified.title || ''} onChange={v => updateGlobal('title', v)} placeholder="ex: Formulaire d'accueil" />
          </FieldFull>
          <FieldFull label="Description (introduction affichée en haut du formulaire)">
            <Textarea value={unified.description || ''} onChange={v => updateGlobal('description', v)} rows={3} />
          </FieldFull>
          <FieldFull label="URL de redirection après soumission (optionnel)">
            <Inp value={unified.redirect_url || ''} onChange={v => updateGlobal('redirect_url', v)}
              placeholder={log.livret_url ? `ex : ${log.livret_url}` : "ex: https://fullbnbzen.com/confirmation"} />
            {/* B1 — pré-remplissage en un clic avec le livret d'accueil du logement
                (log.livret_url, source stable en cfg). Modifiable : un simple clic
                remplit le champ, l'admin peut éditer/vider ensuite. Pas d'écriture
                automatique silencieuse (évite de salir l'état « non enregistré »). */}
            {log.livret_url && unified.redirect_url !== log.livret_url && (
              <button type="button" onClick={() => updateGlobal('redirect_url', log.livret_url)}
                className="fbz-press"
                style={{marginTop:6,display:"inline-flex",alignItems:"center",gap:6,background:DS.card,border:`0.5px solid ${DS.borderHover}`,color:DS.marine,borderRadius:DS.radiusControl,padding:"6px 11px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody}}>
                <CornerDownLeft size={13} strokeWidth={1.8} /> Pré-remplir avec le livret d'accueil
              </button>
            )}
          </FieldFull>
          <CoverPhotoUpload logementId={log.id} value={resolveCoverPhoto(unified)}
            onChange={patch => saveUnified({ ...unified, ...patch })} />
        </SectionCard>

          {/* ═════ Pièce d'identité (indigo) — exigence par canal (ADR-189) ═════ */}
          <SectionCard
            {...FORM_CARD_META.piece}
            subtitle={subWithChip(
              <StatusChip tone={enabledChannels > 0 ? "ciel" : "neutral"}>{enabledChannels}/3 canaux</StatusChip>,
              FORM_CARD_SUBTITLES.piece,
            )}
            collapsible open={openCards.piece} onToggle={() => toggleCard("piece")}
          >
            <IdDocPolicySection policy={idDocPolicy} onToggle={setIdDocChannel} />
          </SectionCard>

          {/* ═════ Liens publics (corail doux) ═════ */}
          <div ref={liensRef}>
            <SectionCard
              {...FORM_CARD_META.liens}
              subtitle={subWithChip(
                <StatusChip tone="corail">/bienvenue</StatusChip>,
                FORM_CARD_SUBTITLES.liens,
              )}
              collapsible open={openCards.liens} onToggle={() => toggleCard("liens")}
            >
              <PublicLinksPanel logementId={log.id} />
            </SectionCard>
          </div>

          {/* ═════ Champs du formulaire (ciel) ═════ */}
          <SectionCard
            {...FORM_CARD_META.champs}
            title={`${FORM_CARD_META.champs.title} — ${fieldCount} champ${fieldCount > 1 ? 's' : ''}`}
            subtitle={FORM_CARD_SUBTITLES.champs}
            collapsible open={openCards.champs} onToggle={() => toggleCard("champs")}
          >
        <div style={{display:"flex",gap:9,alignItems:"flex-start",fontSize:11.5,color:DS.textSecondary,lineHeight:1.6,marginBottom:12,padding:"10px 12px",background:DS.toolBg,borderRadius:8}}>
          <Lightbulb size={15} strokeWidth={1.8} color={DS.marine} style={{flex:"0 0 auto",marginTop:1}} />
          <span>Champs spéciaux côté voyageur : Heure d'arrivée (time_in) / départ (time_out) bornées par les horaires du logement (ADR-072) · Type de pièce (id_type) : Passeport → « Photo passeport » seule ; Carte d'identité → « Recto » + « Verso » · Dates de séjour pré-remplies en lecture seule depuis la résa associée au token.</span>
        </div>
        {/* Regroupement VISUEL (option a) — PARTITION PAR RÉFÉRENCE (décision
            Dimitri) : « Documents légaux » = le module pièce (DOC_FIELD_REFS) ;
            « Coordonnées » = tout le reste (dont billing_address), QUELLE QUE
            SOIT la position. Le modèle plat unified.fields[] n'est PAS touché :
            chaque FieldCard garde son index RÉEL (realIdx) → drag&drop natif
            (reorder de unified.fields[]) inchangé. Un drag inter-groupe re-classe
            le champ dans son groupe de référence au prochain rendu. */}
        {(() => {
          const all = (unified.fields || []).map((f, realIdx) => ({ f, realIdx }))
          const coord = all.filter(({ f }) => !DOC_FIELD_REFS.has(f.reference))
          const docs = all.filter(({ f }) => DOC_FIELD_REFS.has(f.reference))
          const renderCard = ({ f, realIdx }) => (
            <FieldCard key={f.id || realIdx} field={f} idx={realIdx}
              onUpdate={(patch) => updateField(realIdx, patch)}
              onRemove={() => removeField(realIdx)}
              onDrop={(fromIdx) => dropAt(realIdx, fromIdx)}
            />
          )
          return (
            <>
              {coord.length > 0 && <FieldGroupHeader>Coordonnées</FieldGroupHeader>}
              {coord.map(renderCard)}
              {docs.length > 0 && <FieldGroupHeader>Documents légaux</FieldGroupHeader>}
              {docs.map(renderCard)}
            </>
          )
        })()}
        <button onClick={addField} className="fbz-press"
          style={{
            width:"100%",padding:"11px 14px",
            display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,
            border:`1.5px dashed ${DS.borderHover}`,
            background:"transparent",color:DS.marine,
            borderRadius:DS.radiusControl,fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody,
            marginTop:10,
          }}>
          <Plus size={15} strokeWidth={2} /> Ajouter un champ
        </button>
        <div style={{display:"flex",gap:9,alignItems:"flex-start",marginTop:12,fontSize:11,color:DS.textMuted,lineHeight:1.5,padding:"10px 12px",background:DS.toolBg,borderRadius:8}}>
          <Info size={14} strokeWidth={1.8} style={{flex:"0 0 auto",marginTop:1}} />
          <span>Stockage cfg.props[].logs[].forms.unified.fields[] (ADR-071 fusion plate). Anciens types welcome/checkin restent en cfg pour rétrocompat des tokens publics déjà distribués.</span>
        </div>
          </SectionCard>
        </div>{/* fin colonne éditeur */}

        {/* ── Colonne aperçu voyageur (téléphone) — LOT E V4 ──
            RENDU FIDÈLE : monte le VRAI composant du formulaire public
            (GuestFormView, charte voyageur gelée ADR-149) en mode preview inerte.
            Reflète la config en direct (titre, description, champs, ordre, module
            pièce conditionnel, langue). Aucune soumission, aucun token consommé. */}
        <aside style={{ flex: "0 1 380px", width: "100%", maxWidth: 380, position: "sticky", top: 12 }}>
          <FormLivePreview unified={unified} idDocPolicy={idDocPolicy} log={log} />
        </aside>
      </div>{/* fin layout 2 colonnes */}
    </div>
  )
}

// En-tête de groupe visuel des champs (option a) — sépare Coordonnées /
// Documents légaux sans toucher le modèle plat. Miroir léger du GroupTitle
// voyageur (PublicFormPage).
function FieldGroupHeader({ children }) {
  return (
    <div style={{
      fontSize:11, fontWeight:700, color:DS.navy, letterSpacing:.4,
      textTransform:"uppercase", margin:"6px 0 10px", paddingBottom:6,
      borderBottom:`1px solid ${DS.border}`, fontFamily:DS.fontBody,
    }}>
      {children}
    </div>
  )
}

// ── En-tête du formulaire (maquette #2) — statut + CTA « Ouvrir le formulaire ».
// Le CTA déplie la carte « Liens publics » (génération du lien partageable) et y
// défile : aucune action data, pure affordance de navigation.
function FormHeaderBand({ active, onOpen }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      flexWrap: "wrap", gap: 12, marginBottom: 16, padding: "13px 16px",
      background: DS.card, border: `1px solid ${DS.border}`, borderRadius: DS.radiusCard,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
        {catPill(FileText, DS.cielSoft, DS.marine)}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontFamily: DS.fontDisplay, fontSize: 16, fontWeight: 700, color: DS.marineDark }}>
              Formulaire d'arrivée
            </span>
            <StatusChip tone={active ? "success" : "neutral"}>
              {active ? (<><CheckCircle2 size={11} strokeWidth={2.2} /> Actif</>) : "À configurer"}
            </StatusChip>
          </div>
          <div style={{ fontSize: 11, color: DS.textSecondary, marginTop: 2, fontFamily: DS.fontBody }}>
            Formulaire d'accueil unique envoyé au voyageur avant son arrivée.
          </div>
        </div>
      </div>
      <button type="button" onClick={onOpen} className="fbz-press"
        style={{
          display: "inline-flex", alignItems: "center", gap: 7, background: DS.corail,
          color: "#fff", border: "none", borderRadius: DS.radiusControl,
          padding: "9px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
          fontFamily: DS.fontBody,
        }}
        title="Aller aux liens publics : générer / copier le lien à envoyer au voyageur">
        <ExternalLink size={15} strokeWidth={2} /> Ouvrir le formulaire
      </button>
    </div>
  )
}

// ── Aperçu voyageur FIDÈLE (LOT E V4) ───────────────────────────────────────
// Monte le VRAI composant du formulaire public (GuestFormView) avec la config en
// cours d'édition, en mode preview : ce que l'admin valide = ce que le voyageur
// voit (titre, description, champs, ordre, module pièce conditionnel, langue,
// charte navy/sky/terracotta). Zéro fetch de soumission, zéro token consommé.
//
// Écarts assumés vs page publique réelle (sans impact sur la fidélité du RENDU) :
//   - schedule neutre (créneaux horaires pleins) : la page publique borne selon
//     les horaires résolus par /api/form-config depuis le token (non dispo ici) ;
//   - traduction EN/ES = dictionnaire statique seul (staticFieldLabel /
//     staticContentTr), sans l'appel IA /api/form-translate des libellés custom :
//     les libellés custom restent en FR dans l'aperçu (jamais bloquant).
const PREVIEW_CHANNELS = [
  { key: "direct",  label: "Direct" },
  { key: "booking", label: "Booking" },
  { key: "airbnb",  label: "Airbnb" },
]

// Construit le formConfig attendu par GuestFormView depuis la config d'édition.
// Miroir static-first de la projection de PublicFormPage (sans l'IA), + les
// substitutions de variables ({logement_full_name}, …).
function buildPreviewFormConfig(unified, log, lang) {
  const fields = Array.isArray(unified.fields) ? unified.fields : []
  const schedule = { checkin_start: "", checkin_end: "", checkout_time: "" }
  const tr = (s) => {
    if (!s || lang === "fr") return s
    return staticContentTr(lang, s) || s
  }
  const trField = (f) => {
    if (!f || !f.label || lang === "fr") return f
    return { ...f, label: staticFieldLabel(lang, f.reference, f.label) || f.label }
  }
  return {
    title: substituteVars(tr(unified.title), log, schedule, null),
    description: substituteVars(tr(unified.description), log, schedule, null),
    redirect_url: unified.redirect_url || null,
    cover_photo_url: unified.cover_photo_url || null,
    cover_photo_b64: unified.cover_photo_b64 || null,
    fields: fields.map(trField),
  }
}

function FormLivePreview({ unified, idDocPolicy, log }) {
  const [lang, setLang] = useState("fr")
  const [channel, setChannel] = useState("direct")
  const [values, setValues] = useState({})
  const [uploads, setUploads] = useState({})

  const idDocRequired = idDocPolicy[channel] !== false
  // Décision produit 2026-09-06 — l'aperçu reflète aussi le retrait de
  // l'adresse de facturation par canal (Airbnb par défaut), comme le voyageur.
  const billingAddressRequired = getBillingAddressPolicy(log?.forms)[channel] !== false
  const schedule = { checkin_start: "", checkin_end: "", checkout_time: "" }
  const formConfig = buildPreviewFormConfig(unified, log, lang)
  const logementName = log.nom_complet || log.nom || log.id

  const setValue = (ref, v) => setValues((prev) => ({ ...prev, [ref]: v }))
  const changeLang = (loc) => setLang(normalizeFormLocale(loc))
  // Soumission NEUTRALISÉE : aucun appel réseau, aucun token, aucune écriture.
  const submitPreview = (e) => { e.preventDefault() }
  // Upload NEUTRALISÉ : on illustre l'état « envoyé » localement sans réseau.
  const pickIdFile = (ref, file) => {
    setUploads((prev) => {
      const next = { ...prev }
      if (!file) delete next[ref]
      else next[ref] = { status: "done", name: file.name }
      return next
    })
  }
  // ✕ NEUTRALISÉ (aperçu) : retire la ligne localement, aucun appel serveur.
  const removeIdFile = (ref) => {
    setUploads((prev) => { const next = { ...prev }; delete next[ref]; return next })
  }

  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: DS.textMuted, fontFamily: DS.fontBody, margin: "2px 2px 10px" }}>
        Aperçu voyageur
      </div>
      {/* Sélecteur de canal : reflète la règle « pièce d'identité par canal »
          (ADR-189) telle que vue par le voyageur de ce canal. */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        {PREVIEW_CHANNELS.map((ch) => {
          const on = channel === ch.key
          const required = idDocPolicy[ch.key] !== false
          return (
            <button key={ch.key} type="button" onClick={() => setChannel(ch.key)}
              title={required ? "Pièce d'identité demandée pour ce canal" : "Pièce d'identité non demandée pour ce canal"}
              style={{
                flex: 1, padding: "6px 8px", borderRadius: 8, cursor: "pointer",
                fontFamily: DS.fontBody, fontSize: 11, fontWeight: 700,
                border: `1px solid ${on ? DS.marine : DS.border}`,
                background: on ? DS.marine : DS.card,
                color: on ? "#fff" : DS.textSecondary,
              }}>
              {ch.label}
            </button>
          )
        })}
      </div>
      {/* Cadre « téléphone » : scroll interne, le rendu réel étant pleine hauteur. */}
      <div style={{
        borderRadius: 24, border: `1px solid ${DS.border}`, background: "#fff",
        overflow: "hidden", boxShadow: "0 12px 32px rgba(33,83,135,.14)",
        maxHeight: 640, overflowY: "auto",
      }}>
        <GuestFormViewV2
          previewMode
          formConfig={formConfig}
          logementName={logementName}
          reservation={null}
          schedule={schedule}
          idDocRequired={idDocRequired}
          billingAddressRequired={billingAddressRequired}
          values={values}
          uploads={uploads}
          lang={lang}
          step="form"
          error={null}
          onSetValue={setValue}
          onChangeLang={changeLang}
          onSubmit={submitPreview}
          onPickIdFile={pickIdFile}
          onRemoveIdFile={removeIdFile}
        />
      </div>
    </div>
  )
}

// La photo part dans le STOCKAGE de fichiers (bucket public `logements-photos`,
// src/lib/form-cover-photo.js) ; seule son adresse entre dans la configuration.
// Jamais un base64 : deux couvertures ainsi encodées ont fait 97,6 % du blob
// cfg et coupé la base le 2026-09-14. `onChange` reçoit la PATCH à fusionner
// dans forms.unified (adresse + chemin + base64 à null).
function CoverPhotoUpload({ logementId, value, onChange }) {
  const [err, setErr] = useState(null)
  const [envoi, setEnvoi] = useState(false)
  async function pick(file) {
    setErr(null)
    if (!file) return
    setEnvoi(true)
    try {
      const patch = await uploadCoverPhoto(logementId, file)
      onChange(patch)
    } catch (e) {
      console.error('[FormulairesTab] couverture', e)
      setErr(e.message || 'Téléversement échoué')
    } finally {
      setEnvoi(false)
    }
  }
  return (
    <FieldFull label={envoi ? "Photo de couverture — envoi en cours…" : "Photo de couverture (optionnelle, max 5 Mo)"}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        {value ? (
          <>
            <img src={value} alt="couverture" style={{width:200,height:120,objectFit:"cover",borderRadius:8,border:`0.5px solid ${DS.border}`}}/>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,cursor:"pointer",fontSize:11,color:DS.marine,fontWeight:600,padding:"6px 10px",background:DS.cielSoft,borderRadius:DS.radiusControl,textAlign:"center"}}>
                <input type="file" accept="image/*" style={{display:"none"}} onChange={e => pick(e.target.files?.[0])}/>
                <Pencil size={13} strokeWidth={1.8} /> Modifier
              </label>
              <button onClick={() => onChange(clearCoverPhotoPatch())}
                style={{background:"transparent",border:"none",color:DS.danger,fontSize:11,cursor:"pointer",fontFamily:DS.fontBody}}>
                Supprimer
              </button>
            </div>
          </>
        ) : (
          <label style={{display:"inline-flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:11,color:DS.marine,fontWeight:600,padding:"8px 14px",background:DS.cielSoft,borderRadius:DS.radiusControl}}>
            <input type="file" accept="image/*" style={{display:"none"}} onChange={e => pick(e.target.files?.[0])}/>
            <ImagePlus size={14} strokeWidth={1.8} /> Ajouter une photo de couverture
          </label>
        )}
        {err && <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:11,color:DS.danger}}><Info size={13} strokeWidth={1.8} /> {err}</span>}
      </div>
    </FieldFull>
  )
}

// ── IdDocPolicySection (ADR-189 amendement) ────────────────────────────────
// 3 toggles : exiger la pièce d'identité PAR CANAL (Direct / Booking / Airbnb).
// Défauts produit : Direct = requise · Booking = requise · Airbnb = désactivée
// (identité déjà vérifiée par Airbnb). Quand désactivé pour un canal, le module
// pièce (radio + uploads) n'apparaît pas dans le formulaire public et ne bloque
// pas la soumission. Sauvegarde avec le reste de la config via le mécanisme
// d'enregistrement existant (forms.id_doc_policy).
const ID_DOC_CHANNELS = [
  { key: 'direct',  label: 'Réservation directe', hint: 'Voyageur arrivé via votre site / lien direct.' },
  { key: 'booking', label: 'Booking.com',         hint: 'Réservations Booking.com.' },
  { key: 'airbnb',  label: 'Airbnb',              hint: "Identité déjà vérifiée par Airbnb — désactivé par défaut." },
]

function IdDocPolicySection({ policy, onToggle }) {
  // Reskin LOT A : ne wrappe plus dans <Section> — la SectionCard parente porte
  // le titre repliable. Contenu inchangé.
  return (
    <>
      <div style={{fontSize:11,color:"#5a6675",lineHeight:1.6,marginBottom:12}}>
        Choisissez les canaux pour lesquels la pièce d'identité est demandée dans le formulaire d'accueil. Quand désactivée, le voyageur de ce canal n'a ni le choix du type de pièce ni les téléversements — et peut soumettre sans.
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {ID_DOC_CHANNELS.map(ch => (
          <label key={ch.key}
            className="fbz-rowhover"
            style={{
              display:"flex",alignItems:"center",gap:12,padding:"12px 14px",
              border:`0.5px solid ${policy[ch.key] ? DS.ciel : DS.border}`,
              background: policy[ch.key] ? DS.cielSoft : DS.card,
              borderRadius:DS.radiusControl,cursor:"pointer",
            }}>
            <input type="checkbox" checked={!!policy[ch.key]}
              onChange={e => onToggle(ch.key, e.target.checked)}
              style={{width:18,height:18,cursor:"pointer",accentColor:DS.navy}}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:700,color:DS.navy,fontFamily:DS.fontBody}}>
                {ch.label}
              </div>
              <div style={{fontSize:11,color:"#888",marginTop:2,fontFamily:DS.fontBody}}>{ch.hint}</div>
            </div>
            <span style={{fontSize:11,fontWeight:700,color: policy[ch.key] ? DS.success : DS.textMuted,fontFamily:DS.fontBody}}>
              {policy[ch.key] ? 'Requise' : 'Désactivée'}
            </span>
          </label>
        ))}
      </div>
      {/* Note « CB jamais collectée » migrée ici (sa place logique — module pièce). */}
      <div style={{display:"flex",gap:9,alignItems:"flex-start",marginTop:10,fontSize:11,color:DS.warningText,lineHeight:1.5,padding:"9px 12px",background:DS.warningBg,borderRadius:8,borderLeft:`3px solid ${DS.warning}`}}>
        <Info size={14} strokeWidth={1.8} style={{flex:"0 0 auto",marginTop:1}} />
        <span>La carte de crédit n'est jamais collectée par le formulaire : la caution passe par une pré-autorisation Stripe (lien dédié). Les pièces d'identité (passeport, CI) sont uploadées par le voyageur dans un stockage privé — visibles uniquement par l'admin.</span>
      </div>
      <div style={{display:"flex",gap:9,alignItems:"flex-start",marginTop:8,fontSize:11,color:DS.textMuted,lineHeight:1.5,padding:"8px 12px",background:DS.toolBg,borderRadius:8}}>
        <Info size={14} strokeWidth={1.8} style={{flex:"0 0 auto",marginTop:1}} />
        <span>Stockage cfg.props[].logs[].forms.id_doc_policy {`{ direct, booking, airbnb }`}. Sauvegardé avec « Enregistrer » comme le reste de la configuration.</span>
      </div>
    </>
  )
}

function FieldCard({ field, idx, onUpdate, onRemove, onDrop }) {
  const [dragOver, setDragOver] = useState(false)
  const isInput = field.type === 'input'
  const hasOptions = field.type === 'dropdown' || field.type === 'radio' || field.type === 'checkbox'

  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)) }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false)
        const from = parseInt(e.dataTransfer.getData('text/plain'), 10)
        if (!isNaN(from) && from !== idx) onDrop(from)
      }}
      style={{
        border:`0.5px solid ${dragOver ? DS.ciel : DS.border}`,
        background: dragOver ? DS.cielSoft : DS.card,
        borderRadius:DS.radiusControl,padding:12,marginBottom:8,
        display:"grid",gridTemplateColumns:"24px minmax(0,1fr)",gap:10,alignItems:"start",
      }}>
      <div style={{cursor:"grab",color:DS.textMuted,userSelect:"none",lineHeight:1,paddingTop:7,display:"flex"}} title="Glisser pour réorganiser">
        <GripVertical size={18} strokeWidth={1.8} />
      </div>

      <div>
        <div style={{display:"grid",gridTemplateColumns:"minmax(0,2fr) minmax(0,1.5fr) minmax(0,1fr) auto",gap:8,marginBottom:8,alignItems:"end"}}>
          <Field label="Label affiché">
            <Inp value={field.label || ''} onChange={v => onUpdate({ label: v })} placeholder="ex: Email" />
          </Field>
          <Field label="Type de champ">
            <Select value={field.type} onChange={v => onUpdate({ type: v })} options={FIELD_TYPES} />
          </Field>
          <Field label="Référence (clé payload)">
            <Inp value={field.reference || ''} onChange={v => onUpdate({ reference: v.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="email" />
          </Field>
          <button onClick={onRemove} title="Supprimer ce champ" aria-label="Supprimer ce champ"
            className="fbz-press"
            style={{display:"inline-flex",alignItems:"center",justifyContent:"center",background:DS.dangerBg,color:DS.danger,border:"none",borderRadius:DS.radiusControl,padding:"7px 10px",cursor:"pointer",height:32,marginBottom:0}}>
            <Trash2 size={15} strokeWidth={1.8} />
          </button>
        </div>

        <div style={{display:"grid",gridTemplateColumns:isInput ? "minmax(0,1fr) minmax(0,1fr)" : "minmax(0,1fr)",gap:8,alignItems:"center"}}>
          {isInput && (
            <Field label="Type de saisie HTML">
              <Select value={field.input_type || 'text'} onChange={v => onUpdate({ input_type: v })} options={INPUT_TYPES} />
            </Field>
          )}
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#444",fontWeight:600,paddingTop:isInput ? 16 : 0}}>
            <input type="checkbox" checked={!!field.required} onChange={e => onUpdate({ required: e.target.checked })}
              style={{width:16,height:16,cursor:"pointer"}}/>
            Champ obligatoire
          </label>
        </div>

        {hasOptions && (
          <FieldFull label="Options (1 par ligne)">
            <Textarea value={(field.options || []).join('\n')}
              onChange={v => onUpdate({ options: v.split('\n').map(s => s.trim()).filter(Boolean) })}
              placeholder={"Option 1\nOption 2\nOption 3"} rows={3} />
          </FieldFull>
        )}
      </div>
    </div>
  )
}

// ── Phase 2.E.12 — PublicLinksPanel ─────────────────────────────────
// Génération de tokens publics (form_tokens) + liste des liens
// existants pour ce logement × type. Copie URL sur clic.
function PublicLinksPanel({ logementId }) {
  // ADR-065 PR 5 : gère désormais le type 'unified' par défaut.
  // Affiche aussi les anciens tokens 'welcome'/'checkin' (rétrocompat
  // lecture pour les liens publics déjà distribués).
  const [tokens, setTokens] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [info, setInfo] = useState(null)
  const [expiresInDays, setExpiresInDays] = useState(14)
  // B3 — filtre client « actifs seulement » par défaut + bascule historique.
  // Pas d'archivage DB (décision Dimitri) : purement un filtre d'affichage.
  // Un token est « archivé » s'il est expiré OU déjà utilisé.
  const [showHistory, setShowHistory] = useState(false)

  async function reload() {
    setLoading(true); setError(null)
    try {
      const all = await listFormTokens(logementId)
      setTokens(all)
    } catch (e) { setError(e.message || String(e)) }
    finally { setLoading(false) }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload() }, [logementId])

  async function handleCreate() {
    setError(null); setInfo(null); setBusy(true)
    try {
      const tk = await createFormToken({ type: 'unified', logement_id: logementId, expires_in_days: expiresInDays })
      // Le jeton ne peut exister qu'après création : cette copie-ci est
      // structurellement hors geste (déjà le cas avant le lien court). On écrit
      // néanmoins le lien LONG sans autre attente, puis on tente le court.
      const { settled } = copyGuestLinkNow('form', tk.token)
      const { url, copied } = await settled
      setInfo(copied ? `Lien généré et copié : ${url}` : `Lien généré : ${url} (copie manuelle)`)
      await reload()
    } catch (e) { setError(e.message || String(e)) }
    finally { setBusy(false) }
  }

  // ⚠️ NON-ASYNC : l'écriture presse-papier part dans le geste (lien long, ou
  // lien court déjà connu), le lien court la remplace ensuite si possible.
  function handleCopy(token) {
    const { settled } = copyGuestLinkNow('form', token)
    settled.then(({ url, copied }) => {
      setInfo(copied ? `Copié : ${url}` : `URL : ${url} (copie manuelle)`)
    })
  }

  async function handleDelete(token) {
    if (!window.confirm('Révoquer ce lien ? Cette action est définitive — le voyageur ne pourra plus accéder au formulaire via cette URL.')) return
    setError(null); setInfo(null); setBusy(true)
    try {
      await deleteFormToken(token)
      setInfo(`Lien révoqué (${token.slice(0, 8)}…)`)
      await reload()
    } catch (e) { setError(e.message || String(e)) }
    finally { setBusy(false) }
  }

  const TYPE_LABELS = { unified: 'Unifié', welcome: 'Bienvenue (legacy)', checkin: 'Check-in (legacy)' }

  // B3 — partition actifs / archivés (expirés OU utilisés). Le tableau n'affiche
  // que les actifs par défaut ; la bascule révèle l'historique complet.
  const isArchived = (t) => !!t.used_at || !!(t.expires_at && new Date(t.expires_at) < new Date())
  const archivedCount = tokens.filter(isArchived).length
  const visibleTokens = showHistory ? tokens : tokens.filter((t) => !isArchived(t))

  return (
    // Reskin LOT A : ne wrappe plus dans <Section> — la SectionCard parente porte
    // le titre repliable « 🔗 Liens publics ». Contenu (génération + tableau) inchangé.
    <>
      <div style={{fontSize:11,color:"#5a6675",lineHeight:1.6,marginBottom:12}}>
        Génère un lien partageable <code>/bienvenue/&lt;token&gt;</code> à envoyer au voyageur. Le voyageur soumet sans compte FBZ. Les anciens tokens (welcome/checkin) restent valides en rétrocompat — les nouveaux liens utilisent le format unifié.
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:10}}>
        <span style={{fontSize:11,color:"#555"}}>Expire dans</span>
        <input type="number" min="1" max="365" value={expiresInDays} onChange={e => setExpiresInDays(Math.max(1, parseInt(e.target.value, 10) || 14))}
          style={{width:60,padding:"6px 8px",border:"1px solid #dde3ec",borderRadius:6,fontSize:12,textAlign:"center",background:"#f8fafd",fontFamily:DS.fontBody}}/>
        <span style={{fontSize:11,color:"#555"}}>jour(s)</span>
        <button onClick={handleCreate} disabled={busy} className="fbz-press"
          style={{display:"inline-flex",alignItems:"center",gap:6,background: busy ? "#cbd5e1" : DS.corail,color:"#fff",border:"none",borderRadius:DS.radiusControl,padding:"7px 14px",fontSize:12,fontWeight:600,cursor:busy?"not-allowed":"pointer",fontFamily:DS.fontBody,marginLeft:8}}>
          <LinkIcon size={14} strokeWidth={2} /> {busy ? "Génération…" : "Générer un lien"}
        </button>
      </div>
      {error && <div style={{padding:"8px 12px",background:DS.dangerBg,color:DS.dangerText,borderRadius:8,fontSize:11,marginBottom:10}}>{error}</div>}
      {info && <div style={{padding:"8px 12px",background:DS.successBg,color:DS.successText,borderRadius:8,fontSize:11,marginBottom:10,wordBreak:"break-all"}}>{info}</div>}

      {/* B3 — bascule historique (n'apparaît que s'il y a des liens archivés). */}
      {!loading && archivedCount > 0 && (
        <label style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:"#555",cursor:"pointer",marginBottom:10}}>
          <input type="checkbox" checked={showHistory} onChange={e => setShowHistory(e.target.checked)}
            style={{width:15,height:15,cursor:"pointer",accentColor:DS.navy}}/>
          Afficher l'historique ({archivedCount} lien{archivedCount > 1 ? 's' : ''} expiré{archivedCount > 1 ? 's' : ''} ou utilisé{archivedCount > 1 ? 's' : ''})
        </label>
      )}

      {loading ? <div style={{fontSize:11,color:"#888"}}>Chargement…</div> : (
        visibleTokens.length === 0 ? (
          <div style={{fontSize:11,color:"#aaa",fontStyle:"italic"}}>
            {tokens.length === 0
              ? 'Aucun lien public généré pour ce logement.'
              : `Aucun lien actif. ${archivedCount} lien${archivedCount > 1 ? 's' : ''} dans l'historique (cochez ci-dessus pour les voir).`}
          </div>
        ) : (
          <div className="fbz-table-wrap">
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
            <thead><tr style={{background:DS.toolBg}}>
              <th style={{padding:"6px 8px",textAlign:"left",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.4,fontWeight:700}}>Type</th>
              <th style={{padding:"6px 8px",textAlign:"left",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.4,fontWeight:700}}>Token</th>
              <th style={{padding:"6px 8px",textAlign:"left",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.4,fontWeight:700}}>Créé le</th>
              <th style={{padding:"6px 8px",textAlign:"left",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.4,fontWeight:700}}>Expire</th>
              <th style={{padding:"6px 8px",textAlign:"left",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.4,fontWeight:700}}>Utilisé</th>
              <th style={{padding:"6px 8px",textAlign:"center",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.4,fontWeight:700}}>Actions</th>
            </tr></thead>
            <tbody>
              {visibleTokens.map(t => {
                const expired = t.expires_at && new Date(t.expires_at) < new Date()
                return (
                  <tr key={t.token} style={{borderBottom:"1px solid #f4f7fb",opacity: expired ? 0.5 : 1}}>
                    <td style={{padding:"6px 8px",fontSize:10,color:"#555",fontWeight:600}}>{TYPE_LABELS[t.type] || t.type}</td>
                    <td style={{padding:"6px 8px",fontFamily:"monospace",fontSize:10,color:"#555"}}>{t.token.slice(0, 8)}…{t.token.slice(-4)}</td>
                    <td style={{padding:"6px 8px",color:"#666"}}>{new Date(t.created_at).toLocaleDateString('fr-FR')}</td>
                    <td style={{padding:"6px 8px",color: expired ? DS.red : "#666"}}>
                      {t.expires_at ? new Date(t.expires_at).toLocaleDateString('fr-FR') : '∞'}
                      {expired && ' (expiré)'}
                    </td>
                    <td style={{padding:"6px 8px",color: t.used_at ? DS.green : "#aaa"}}>
                      {t.used_at ? `✓ ${new Date(t.used_at).toLocaleDateString('fr-FR')}` : '—'}
                    </td>
                    <td style={{padding:"6px 8px",textAlign:"center"}}>
                      <div style={{display:"flex",gap:4,justifyContent:"center",flexWrap:"wrap"}}>
                        <a href={publicFormUrl(t.token)} target="_blank" rel="noopener noreferrer"
                          onClick={expired ? (e) => e.preventDefault() : undefined}
                          style={{display:"inline-flex",alignItems:"center",gap:4,background:DS.cielSoft,color: expired ? DS.textMuted : DS.marine,border:"none",borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:600,cursor:expired?"not-allowed":"pointer",fontFamily:DS.fontBody,textDecoration:"none"}}
                          title={expired ? 'Lien expiré' : 'Ouvrir dans un nouvel onglet'}>
                          <Eye size={12} strokeWidth={1.8} /> Voir
                        </a>
                        <button onClick={() => handleCopy(t.token)} disabled={expired}
                          style={{display:"inline-flex",alignItems:"center",gap:4,background:DS.cielSoft,color:DS.marine,border:"none",borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:600,cursor:expired?"not-allowed":"pointer",fontFamily:DS.fontBody}}>
                          <Copy size={12} strokeWidth={1.8} /> Copier URL
                        </button>
                        <button onClick={() => handleDelete(t.token)} disabled={busy}
                          title="Révoquer ce lien (définitif)"
                          style={{display:"inline-flex",alignItems:"center",gap:4,background:DS.dangerBg,color:DS.danger,border:"none",borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:600,cursor:busy?"not-allowed":"pointer",fontFamily:DS.fontBody}}>
                          <Trash2 size={12} strokeWidth={1.8} /> Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        )
      )}
    </>
  )
}
